import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { ChevronDown } from 'lucide-react';

interface Language {
  code: string;
  name: string;
  shortName: string;
}

const languages: Language[] = [
  { code: 'es', name: 'Español', shortName: 'ES' },
  { code: 'en', name: 'English', shortName: 'EN' }
];

interface LanguageSelectorProps {
  className?: string;
  variant?: 'default' | 'compact';
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  className = '', 
  variant = 'default' 
}) => {
  const { currentLanguage, changeLanguage } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedLanguage = languages.find(lang => lang.code === currentLanguage) || languages[0];

  const handleLanguageChange = (languageCode: string): void => {
    changeLanguage(languageCode);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Extract text color from className if provided, otherwise use default
  const textColorClass = className.includes('text-') ? className : 'text-inherit';

  if (variant === 'compact') {
    return (
      <div className={`relative inline-block ${className}`} ref={dropdownRef}>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          type="button"
          className={`flex items-center gap-1 bg-transparent border-none focus:outline-none cursor-pointer transition-opacity duration-200 hover:opacity-80 ${textColorClass}`}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <span className="text-sm font-medium">{selectedLanguage.shortName}</span>
          <ChevronDown 
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
        
        {isOpen && (
          <div 
            className="absolute right-0 mt-2 py-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[120px]"
            style={{ 
              zIndex: 99999,
              position: 'absolute'
            }}
          >
            {languages.map((language) => (
              <button
                key={language.code}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLanguageChange(language.code);
                }}
                className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150 ${
                  language.code === currentLanguage ? 'bg-gray-50 font-medium' : ''
                }`}
              >
                {language.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        type="button"
        className={`flex items-center gap-1 bg-transparent border-none focus:outline-none cursor-pointer transition-opacity duration-200 hover:opacity-80 ${textColorClass}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="text-sm font-medium">{selectedLanguage.shortName}</span>
        <ChevronDown 
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 py-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[120px]"
          style={{ 
            zIndex: 99999,
            position: 'absolute'
          }}
        >
          {languages.map((language) => (
            <button
              key={language.code}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleLanguageChange(language.code);
              }}
              className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150 ${
                language.code === currentLanguage ? 'bg-gray-50 font-medium' : ''
              }`}
            >
              {language.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
